import type { Location } from '@up2stay/up2promo-api-client';
import {
  Badge,
  Button,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@up2stay/up2promo-ui';
import { ChevronDown, ChevronUp, Plus, Trash2 } from 'lucide-react';
import { type ReactNode, useMemo, useState } from 'react';

import {
  SettingsGroup,
  SettingsGroupRow,
  SettingsGroupRows,
} from '@/core/components/settings/settings-group';

export type SetupOverrideListLabels = {
  empty: string;
  add: string;
  remove: string;
  overrideTag: string;
  location: string;
  locationPlaceholder: string;
  continue: string;
  cancel: string;
};

/**
 * Per-location overrides of a workspace default (reply style, auto-replies,
 * industry preset). Overrides are expandable rows; picking a location opens
 * its editor and it becomes an override once something is saved.
 */
export function SetupOverrideList({
  locations,
  isOverride,
  initialLocationId,
  labels,
  renderEditor,
  onRemove,
  removing,
}: {
  locations: Location[];
  /** Whether a location already deviates from the workspace default. */
  isOverride: (location: Location) => boolean;
  /** Deep-linked location (`?locationId=`) — opened automatically. */
  initialLocationId?: string;
  labels: SetupOverrideListLabels;
  renderEditor: (location: Location) => ReactNode;
  onRemove: (locationId: string) => void;
  removing: boolean;
}) {
  const overrides = useMemo(
    () => locations.filter(isOverride),
    [locations, isOverride],
  );
  const available = useMemo(
    () => locations.filter((loc) => !isOverride(loc)),
    [locations, isOverride],
  );

  const [userExpandedId, setUserExpandedId] = useState<string | null>(null);
  const expandedId =
    initialLocationId && overrides.some((l) => l.id === initialLocationId)
      ? initialLocationId
      : userExpandedId;
  const [addOpen, setAddOpen] = useState(false);
  const [pickId, setPickId] = useState('');

  // A location chosen for a new override, not yet persisted as one.
  const pendingId = useMemo(() => {
    for (const id of [expandedId, initialLocationId]) {
      if (
        id &&
        !overrides.some((l) => l.id === id) &&
        locations.some((l) => l.id === id)
      ) {
        return id;
      }
    }
    return null;
  }, [expandedId, initialLocationId, overrides, locations]);
  const pending = pendingId ? locations.find((l) => l.id === pendingId) : null;

  const addButton = (
    <Button
      variant="ghostAccent"
      size="sm"
      disabled={available.length === 0}
      onClick={() => setAddOpen(true)}
    >
      <Plus className="mr-1.5 size-4" aria-hidden />
      {labels.add}
    </Button>
  );

  const hasRows = overrides.length > 0 || pending != null;

  return (
    <div className="flex flex-col gap-2">
      {!hasRows && !addOpen ? (
        <div className="flex flex-wrap items-center gap-3 rounded-[var(--r-settings-group,12px)] border border-dashed px-[18px] py-3.5">
          <p className="text-muted-foreground min-w-0 flex-1 text-[13px]">
            {labels.empty}
          </p>
          {addButton}
        </div>
      ) : (
        <SettingsGroup>
          <SettingsGroupRows>
            {overrides.map((loc, index) => {
              const expanded = expandedId === loc.id;
              return (
                <SettingsGroupRow key={loc.id} separated={index > 0}>
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        className="flex min-w-0 flex-1 items-center gap-2.5 text-left"
                        aria-expanded={expanded}
                        onClick={() =>
                          setUserExpandedId(expanded ? null : loc.id)
                        }
                      >
                        {expanded ? (
                          <ChevronUp className="size-4 shrink-0" aria-hidden />
                        ) : (
                          <ChevronDown
                            className="size-4 shrink-0"
                            aria-hidden
                          />
                        )}
                        <Badge variant="secondary" className="text-[10.5px]">
                          {labels.overrideTag}
                        </Badge>
                        <span className="truncate text-sm font-semibold">
                          {loc.name}
                        </span>
                      </button>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={labels.remove}
                        onClick={() => onRemove(loc.id)}
                        disabled={removing}
                      >
                        <Trash2 className="size-4" aria-hidden />
                      </Button>
                    </div>
                    {expanded ? renderEditor(loc) : null}
                  </div>
                </SettingsGroupRow>
              );
            })}

            {pending ? (
              <SettingsGroupRow separated={overrides.length > 0}>
                <div className="flex flex-col gap-3">
                  <p className="text-sm font-semibold">{pending.name}</p>
                  {renderEditor(pending)}
                </div>
              </SettingsGroupRow>
            ) : null}

            {addOpen ? (
              <SettingsGroupRow separated={hasRows}>
                <div className="flex max-w-md flex-col gap-3">
                  <Label>{labels.location}</Label>
                  <Select value={pickId} onValueChange={setPickId}>
                    <SelectTrigger>
                      <SelectValue placeholder={labels.locationPlaceholder} />
                    </SelectTrigger>
                    <SelectContent>
                      {available.map((loc) => (
                        <SelectItem key={loc.id} value={loc.id}>
                          {loc.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex gap-2">
                    <Button
                      disabled={!pickId}
                      onClick={() => {
                        setAddOpen(false);
                        setUserExpandedId(pickId);
                        setPickId('');
                      }}
                    >
                      {labels.continue}
                    </Button>
                    <Button variant="outline" onClick={() => setAddOpen(false)}>
                      {labels.cancel}
                    </Button>
                  </div>
                </div>
              </SettingsGroupRow>
            ) : hasRows ? (
              <SettingsGroupRow separated>{addButton}</SettingsGroupRow>
            ) : null}
          </SettingsGroupRows>
        </SettingsGroup>
      )}
    </div>
  );
}
